import { Table, TableBody, TableHead, TableRow, Tooltip, Typography } from '@mui/material';

import ClientPaginatedTable from '../table/ClientPaginatedTable';
import ActionIconButton from '../table/ActionIconButton';
import GeneralTableBox from '../table/GeneralTableBox';
import HeadTableCell from '../table/HeadTableCell';
import RowTableCell from '../table/RowTableCell';
import NoDataSkeleton from '../skeletons/NoDataSkeleton';
import DecimalTextField from '../inputs/DecimalTextField';
import { MATERIAL_QUANTITY_TABLE_TEXTS as TEXTS } from './materialPresentationConstants';
import { TABLE_INPUT_CELL_SX, TABLE_INPUT_SX } from '../../styles/tableInputs';

const MIN_TABLE_WIDTH = 640;
const QUANTITY_INPUT_PROPS = { style: { textAlign: 'right' } };
const textSx = { textAlign: 'left' };
const numberSx = { textAlign: 'right' };

/**
 * Tabla editable de materiales y cantidades (formularios de pedidos y órdenes), con el formato de
 * Finanzas: anchos fijos, campo de cantidad alineado en la fila y quitar con ícono y tooltip.
 * @param {Object} props
 * @param {Array<Object>} props.rows Renglones
 *   `{ key, code, name, detail?, unit?, quantity, readOnly? }`.
 * @param {Function} props.onQuantityChange Recibe (key, valor) al cambiar la cantidad.
 * @param {Function} props.onRemove Recibe la key del renglón a quitar.
 * @param {string} [props.emptyMessage] Texto cuando no hay renglones.
 * @param {boolean} [props.disabled] Deshabilita la edición.
 * @returns {JSX.Element}
 */
const MaterialQuantityTable = ({
  rows = [],
  onQuantityChange,
  onRemove,
  emptyMessage = TEXTS.empty,
  disabled = false,
}) => (
  <ClientPaginatedTable rows={rows}>
    {(pageRows) => (
      <GeneralTableBox hasMaxHeight={false} fixedLayout minTableWidth={MIN_TABLE_WIDTH}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <HeadTableCell sx={textSx}>{TEXTS.material}</HeadTableCell>
              <HeadTableCell sx={{ ...textSx, width: 130 }}>{TEXTS.unit}</HeadTableCell>
              <HeadTableCell sx={{ ...numberSx, width: 140 }}>{TEXTS.quantity}</HeadTableCell>
              <HeadTableCell sx={{ width: 110 }}>{TEXTS.actions}</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length ? (
              pageRows.map((row) => (
                <TableRow key={row.key} hover>
                  <RowTableCell sx={textSx} title={row.name}>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }} noWrap>
                      {row.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                      {[row.code, row.detail].filter(Boolean).join(' · ')}
                    </Typography>
                  </RowTableCell>
                  <RowTableCell sx={textSx}>{row.unit || '-'}</RowTableCell>
                  <RowTableCell sx={{ ...numberSx, ...TABLE_INPUT_CELL_SX }}>
                    <DecimalTextField
                      size="small"
                      value={row.quantity}
                      onChange={(event) => onQuantityChange(row.key, event.target.value)}
                      inputProps={QUANTITY_INPUT_PROPS}
                      disabled={disabled || row.readOnly}
                      sx={TABLE_INPUT_SX}
                    />
                  </RowTableCell>
                  <RowTableCell>
                    <Tooltip title={TEXTS.remove}>
                      <span>
                        <ActionIconButton
                          variant="delete"
                          onClick={() => onRemove(row.key)}
                          disabled={disabled || row.readOnly}
                        />
                      </span>
                    </Tooltip>
                  </RowTableCell>
                </TableRow>
              ))
            ) : (
              <NoDataSkeleton colSpan={4} message={emptyMessage} showSubtitle={false} />
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>
    )}
  </ClientPaginatedTable>
);

export default MaterialQuantityTable;
